import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { BookOpen, Loader2 } from 'lucide-react';
import { Blog } from '../types/blog';
import { BlogCard } from '../components/Blog/BlogCard';
import { Pagination } from '../components/Blog/Pagination';
import { Navbar } from '../components/Navbar/Navbar';
import { trackButtonClick } from '../firebaseConfig';
import { useAnalyticsTracking } from '../hooks/useAnalyticsTracking';
import { ParticleBackground } from '../components/ParticleBackground';
import { blogService } from '../services/blogService';

interface BlogListPageState {
  blogs: Blog[];
  isLoading: boolean;
  totalBlogs: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export default function BlogListPage() {
  const [state, setState] = useState<BlogListPageState>({
    blogs: [],
    isLoading: true,
    totalBlogs: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [animationPhase, setAnimationPhase] = useState(0);
  const { trackSectionView } = useAnalyticsTracking();
  const blogsPerPage = 6;

  useEffect(() => {
    // Set document title
    document.title = 'Blogs | Read More - Learn More With MockTrail';
    
    // Force scroll to top on page load
    window.scrollTo(0, 0);
    
    // Start animation
    setTimeout(() => setAnimationPhase(1), 1600);

    // Track page view
    trackButtonClick('Blog List Page Viewed', 'Blog List Page');
  }, []); // Empty dependency array - only run once

  // Track analytics separately to prevent render loops
  useEffect(() => {
    if (!state.isLoading) {
      trackSectionView('Blog List Page', {
        total_blogs: state.totalBlogs,
        current_page: currentPage,
        blogs_per_page: blogsPerPage,
      });
    }
  }, [state.isLoading, state.totalBlogs, currentPage]); // Only track when state changes

  // Fetch blogs when page changes
  useEffect(() => {
    const fetchBlogs = async () => {
      setState(prev => ({ ...prev, isLoading: true }));
      
      try {
        // Validate parameters before API call
        const validPage = Math.max(1, currentPage);
        const validLimit = Math.min(50, Math.max(1, blogsPerPage));
        
        // Try API first
        const apiResponse = await blogService.getPublishedBlogs({
          page: validPage,
          limit: validLimit
        });
        console.log('fetchBlogs apiResponse', apiResponse);
        // Check if API returned valid data
        if (apiResponse.blogs && apiResponse.blogs.length > 0) {
          setState(prev => ({
            ...prev,
            blogs: apiResponse.blogs,
            totalBlogs: apiResponse.total,
            totalPages: apiResponse.totalPages,
            hasNext: apiResponse.hasNext,
            hasPrev: apiResponse.hasPrev,
            isLoading: false,
          }));
          return;
        }
        
        // If API returns empty data or fails, fall back to mock data
        throw new Error('API returned empty data, falling back to mock data');
        
      } catch (error) {
        console.warn('API call failed, using mock data:', error);
      }
      finally{
        setState(prev => ({
          ...prev,
          isLoading: false,
        }));
      }
    };

    fetchBlogs();
  }, [currentPage, blogsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Blogs | Read More - Learn More With MockTrail</title>
        <meta name="description" content="Discover expert insights, interview tips, and career advice to excel in your professional journey." />
        <meta name="keywords" content="interview tips, career advice, job preparation, technical interviews, soft skills, professional development" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Blogs | Read More - Learn More With MockTrail" />
        <meta property="og:description" content="Discover expert insights, interview tips, and career advice to excel in your professional journey." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blogs | Read More - Learn More With MockTrail" />
        <meta name="twitter:description" content="Discover expert insights, interview tips, and career advice to excel in your professional journey." />
        
        {/* Canonical URL */}
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <div className="min-h-screen bg-background-light dark:bg-background-dark">
        {/* Background Effects */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
          <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <ParticleBackground />
        <Navbar />

        <main className="relative z-10 pt-48 pb-12 max-w-7xl mx-auto">
          <div className="container mx-auto px-4">
            {/* Page Header */}
            <div className={`text-center mb-16 md:mb-20 transform transition-all duration-500 ease-out ${
              animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}>
              
              <h1 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark mb-6">
                Read More - Learn More With MockTrail
              </h1>
              
              <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto">
                Comprehensive guides, expert tips, and industry insights to help you excel in interviews and advance your career.
              </p>
            </div>

            {/* Results Info */}
            <div className={`mb-8 transform transition-all duration-500 ease-out ${
              animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`} style={{ transitionDelay: '100ms' }}>
              <div className="flex items-center justify-between">
                <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  {state.isLoading ? (
                    <span>Loading articles...</span>
                  ) : state.blogs.length > 0 ? (
                    <span>
                      Showing {state.blogs.length} of {state.totalBlogs} articles
                    </span>
                  ) : (
                    <span></span>
                  )}
                </div>
                
              </div>
            </div>

            {/* Loading State */}
            {state.isLoading && (
              <div className={`flex justify-center items-center py-20 transform transition-all duration-500 ease-out ${
                animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`} style={{ transitionDelay: '200ms' }}>
                <Loader2 className="w-12 h-12 text-primary-light dark:text-primary-dark animate-spin" />
              </div>
            )}

            {/* No Results */}
            {!state.isLoading && state.blogs.length === 0 && (
              <div className={`text-center py-20 transform transition-all duration-500 ease-out ${
                animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`} style={{ transitionDelay: '200ms' }}>
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-primary-light dark:text-primary-dark" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                  No articles found
                </h3>
                <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-6">
                  Check back soon for new content!
                </p>
              </div>
            )}

            {/* Blogs Grid */}
            {!state.isLoading && state.blogs.length > 0 && (
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 auto-rows-fr transform transition-all duration-500 ease-out ${
                animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`} style={{ transitionDelay: '300ms' }}>
                {state.blogs.map((blog, index) => (
                  <div
                    key={blog._id}
                    className="transform transition-all duration-300 hover:scale-[1.02] h-full"
                    style={{ 
                      animationDelay: `${index * 50 + 350}ms`,
                    }}
                  >
                    <BlogCard 
                      blog={blog} 
                      variant="default"
                      showAuthor={false}
                      showViewCount={false}
                      showTags={true}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {!state.isLoading && state.blogs.length > 0 && state.totalPages > 1 && (
              <div className={`transform transition-all duration-500 ease-out ${
                animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`} style={{ transitionDelay: '400ms' }}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={state.totalPages}
                  hasNext={state.hasNext}
                  hasPrev={state.hasPrev}
                  onPageChange={handlePageChange}
                  isLoading={state.isLoading}
                />
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
} 